'use client'

import React, { useMemo, useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useRegion } from '@/shared/hooks/use-region'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Users,
  CheckCircle2,
  Clock,
  Plus,
  Minus,
  MapPin,
  Calendar,
  User,
  GripHorizontal,
  UserPlus,
  X,
  Search,
  UserCheck,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import { buildRegionUrl, cn } from '@/shared/lib/utils'
import {
  setActiveJobBoardDrag,
  getActiveJobBoardDrag,
} from '../../constants/job-board-dnd'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { JobBoardTeam } from '../../api/work-orders.api'

interface WeeklyJobBoardCalendarProps {
  weekDates: Array<{ date: string; dayName: string; dayNumber: string; isToday: boolean }>
  workOrders: any[]
  candidateTeamLeads: any[]
  allVolunteers?: any[]
  designatedLeaderIds?: number[]
  teams?: JobBoardTeam[]
  isPlanner: boolean
  isTeamLead: boolean
  currentUserId?: number
  onOpenStaffingModal: (workOrder: any, date: string) => void
  onOpenApprovalModal: (workOrder: any) => void
  onOpenManageRoster?: (team: any) => void
  onUpdateWorkOrderDates: (workOrderId: number, newDates: string[]) => void
  onAssignTeamLead: (workOrderId: number, teamLeadId: number | null) => void
  onMoveWorkOrder: (
    workOrderId: number,
    targetTeam: JobBoardTeam | null,
    newDates: string[]
  ) => void
  onDropNewWorkScope?: (
    scopeItem: any,
    targetTeam: JobBoardTeam | null,
    date: string
  ) => void
  onAssignLeaderToTeam?: (teamId: number, leaderId: number) => void
  onDeleteTeam?: (teamId: number) => void
  onAddLeaderRow?: (userId: number) => void
  onRemoveLeaderRow?: (userId: number) => void
}

export function WeeklyJobBoardCalendar({
  weekDates,
  workOrders,
  candidateTeamLeads,
  allVolunteers = [],
  designatedLeaderIds = [],
  teams = [],
  isPlanner,
  isTeamLead,
  currentUserId,
  onOpenStaffingModal,
  onOpenApprovalModal,
  onOpenManageRoster,
  onUpdateWorkOrderDates,
  onAssignTeamLead,
  onMoveWorkOrder,
  onDropNewWorkScope,
  onAssignLeaderToTeam,
  onDeleteTeam,
  onAddLeaderRow,
  onRemoveLeaderRow,
}: WeeklyJobBoardCalendarProps) {
  const router = useRouter()
  const { regionSlug } = useRegion()

  const [dragOverCell, setDragOverCell] = useState<{
    rowId: string
    dayIndex: number
    date: string
  } | null>(null)

  const [draggingWorkOrderId, setDraggingWorkOrderId] = useState<number | null>(null)
  const [assigningLeaderForTeamId, setAssigningLeaderForTeamId] = useState<number | null>(null)
  const [assigningLeaderForWorkOrderId, setAssigningLeaderForWorkOrderId] = useState<
    number | 'ALL_UNASSIGNED' | null
  >(null)
  const [leaderSearch, setLeaderSearch] = useState('')

  // Custom vertical order overrides per row: { [rowId]: [workOrderId1, workOrderId2, ...] }
  const [customVerticalOrders, setCustomVerticalOrders] = useState<Record<string, number[]>>({})

  useEffect(() => {
    try {
      const saved = localStorage.getItem('job_board_vertical_orders')
      if (saved) {
        setCustomVerticalOrders(JSON.parse(saved))
      }
    } catch {}
  }, [])

  const saveVerticalOrder = (rowId: string, orderedIds: number[]) => {
    const next = { ...customVerticalOrders, [rowId]: orderedIds }
    setCustomVerticalOrders(next)
    try {
      localStorage.setItem('job_board_vertical_orders', JSON.stringify(next))
    } catch {}
  }

  // Get active dates for a work order
  const getWorkOrderDates = (wo: any): string[] => {
    if (Array.isArray(wo.workOrderHours) && wo.workOrderHours.length > 0) {
      return wo.workOrderHours.map((h: any) => h.date)
    }
    if (wo.dateDelegated) {
      return [wo.dateDelegated]
    }
    return []
  }

  // Group work orders by Team (plus unassigned jobs at bottom)
  const groupedByTeam = useMemo(() => {
    const teamEntries: Array<{
      id: string
      team: JobBoardTeam | null
      teamName: string
      lead: { id: number; fullname: string; role?: any } | null
      workOrders: any[]
    }> = teams.map((t) => {
      const leadVol = t.teamLeadId
        ? allVolunteers.find((v) => v.userId === t.teamLeadId || v.id === t.teamLeadId)
        : null
      const leadObj = t.teamLeadId
        ? leadVol
          ? {
              id: leadVol.userId || leadVol.id,
              fullname: leadVol.fullName,
              role: leadVol.role || 'Team Leader',
            }
          : t.teamLead
          ? {
              id: t.teamLead.id,
              fullname: t.teamLead.fullName || (t.teamLead as any).fullname || 'Team Leader',
              role: t.teamLead.role,
            }
          : { id: t.teamLeadId, fullname: `Leader #${t.teamLeadId}`, role: 'Team Leader' }
        : null

      return {
        id: `team_${t.id}`,
        team: t,
        teamName: t.teamName || (leadObj ? `${leadObj.fullname}'s Team` : `Team #${t.id}`),
        lead: leadObj,
        workOrders: [] as any[],
      }
    })

    const unassignedWos: any[] = []

    workOrders.forEach((wo) => {
      let matchedEntry: (typeof teamEntries)[0] | undefined

      // Match by teamName first
      if (wo.teamName) {
        matchedEntry = teamEntries.find(
          (e) =>
            e.team?.teamName &&
            e.team.teamName.trim().toLowerCase() === wo.teamName.trim().toLowerCase()
        )
      }

      // If not matched by teamName, match by teamLeadId
      if (!matchedEntry && wo.teamLead?.id) {
        matchedEntry = teamEntries.find(
          (e) => e.team?.teamLeadId === wo.teamLead.id
        )
      }

      if (matchedEntry) {
        matchedEntry.workOrders.push(wo)
      } else {
        unassignedWos.push(wo)
      }
    })

    const list = [...teamEntries]

    // Unassigned row at the bottom
    list.push({
      id: 'unassigned',
      team: null,
      teamName: 'Unassigned Jobs',
      lead: null,
      workOrders: unassignedWos,
    })

    return list
  }, [workOrders, teams, allVolunteers])

  // Shift work order up in vertical lane priority
  const handleMoveOrderVertically = (rowId: string, workOrderId: number, direction: 'up' | 'down') => {
    const rowWos = groupedByTeam.find((g) => g.id === rowId)?.workOrders || []
    const currentOrder = customVerticalOrders[rowId]
      ? [...customVerticalOrders[rowId]]
      : rowWos.map((w) => w.id)

    // Ensure all current work order IDs are present in the list
    rowWos.forEach((w) => {
      if (!currentOrder.includes(w.id)) currentOrder.push(w.id)
    })

    const idx = currentOrder.indexOf(workOrderId)
    if (idx === -1) return

    if (direction === 'up' && idx > 0) {
      const temp = currentOrder[idx]
      currentOrder[idx] = currentOrder[idx - 1]
      currentOrder[idx - 1] = temp
      saveVerticalOrder(rowId, currentOrder)
    } else if (direction === 'down' && idx < currentOrder.length - 1) {
      const temp = currentOrder[idx]
      currentOrder[idx] = currentOrder[idx + 1]
      currentOrder[idx + 1] = temp
      saveVerticalOrder(rowId, currentOrder)
    }
  }

  // Multi-Lane Gantt placement algorithm for a team's work orders:
  // Sorts by earliest start date first, then packs into non-overlapping horizontal tracks (lanes)
  const computeTeamLanes = (rowId: string, rowWorkOrders: any[]) => {
    const customOrder = customVerticalOrders[rowId] || []

    // Map each work order with its week bounds
    const mapped = rowWorkOrders.map((wo) => {
      const dates = getWorkOrderDates(wo)
      const matchingIndices = dates
        .map((d) => weekDates.findIndex((w) => w.date === d))
        .filter((idx) => idx !== -1)
        .sort((a, b) => a - b)

      const startIndex = matchingIndices.length > 0 ? matchingIndices[0] : 0
      const endIndex =
        matchingIndices.length > 0 ? matchingIndices[matchingIndices.length - 1] : startIndex

      const colStart = startIndex + 1
      const colSpan = Math.max(1, endIndex - startIndex + 1)
      const customRank = customOrder.indexOf(wo.id)

      return {
        wo,
        dates,
        startIndex,
        endIndex,
        colStart,
        colSpan,
        customRank: customRank === -1 ? 999 : customRank,
      }
    })

    // Sort: If custom vertical ranking is set, rank by that.
    // Otherwise, strictly sort by EARLIEST START DATE (startIndex ASC) so earliest start is at the top!
    mapped.sort((a, b) => {
      if (customOrder.length > 0 && a.customRank !== b.customRank) {
        return a.customRank - b.customRank
      }
      if (a.startIndex !== b.startIndex) {
        return a.startIndex - b.startIndex
      }
      if (a.endIndex !== b.endIndex) {
        return a.endIndex - b.endIndex
      }
      return a.wo.id - b.wo.id
    })

    // Greedily pack items into non-overlapping horizontal lane tracks
    const lanes: Array<Array<{ startIndex: number; endIndex: number }>> = []
    const placedItems = mapped.map((item) => {
      let assignedLane = -1

      for (let l = 0; l < lanes.length; l++) {
        const laneHasCollision = lanes[l].some(
          (occupied) => !(item.endIndex < occupied.startIndex || item.startIndex > occupied.endIndex)
        )
        if (!laneHasCollision) {
          assignedLane = l
          lanes[l].push({ startIndex: item.startIndex, endIndex: item.endIndex })
          break
        }
      }

      if (assignedLane === -1) {
        assignedLane = lanes.length
        lanes.push([{ startIndex: item.startIndex, endIndex: item.endIndex }])
      }

      return {
        ...item,
        laneIndex: assignedLane,
      }
    })

    return {
      placedItems,
      totalLanes: Math.max(1, lanes.length),
    }
  }

  // Extend duration (+1 day)
  const handleExtendDuration = (e: React.MouseEvent, wo: any) => {
    e.stopPropagation()
    const dates = getWorkOrderDates(wo)
    if (dates.length === 0) {
      onUpdateWorkOrderDates(wo.id, [weekDates[0].date])
      return
    }
    const lastDate = dates[dates.length - 1]
    const lastIndex = weekDates.findIndex((d) => d.date === lastDate)
    if (lastIndex >= 0 && lastIndex < weekDates.length - 1) {
      const nextDate = weekDates[lastIndex + 1].date
      if (!dates.includes(nextDate)) {
        onUpdateWorkOrderDates(wo.id, [...dates, nextDate])
      }
    }
  }

  // Shorten duration (-1 day)
  const handleShortenDuration = (e: React.MouseEvent, wo: any) => {
    e.stopPropagation()
    const dates = getWorkOrderDates(wo)
    if (dates.length > 1) {
      onUpdateWorkOrderDates(wo.id, dates.slice(0, -1))
    }
  }

  // Helper to calculate day index from mouse X position relative to track container
  const computeDayIndex = (
    clientX: number,
    currentTarget: HTMLElement
  ): number => {
    const rect = currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(rect.width - 1, clientX - rect.left))
    const colWidth = rect.width / 6
    return Math.min(5, Math.max(0, Math.floor(x / colWidth)))
  }

  const availableVolunteers = allVolunteers.filter((v) => {
    if (leaderSearch.trim()) {
      const q = leaderSearch.toLowerCase()
      return (
        (v.fullName || v.fullname || '').toLowerCase().includes(q) ||
        (v.groupName || '').toLowerCase().includes(q)
      )
    }
    return true
  })

  const handleSelectLeaderForTeam = (leaderUserId: number) => {
    if (assigningLeaderForTeamId && onAssignLeaderToTeam) {
      onAssignLeaderToTeam(assigningLeaderForTeamId, leaderUserId)
    }
    setAssigningLeaderForTeamId(null)
  }

  const handleSelectLeaderForWorkOrder = (leaderUserId: number) => {
    if (assigningLeaderForWorkOrderId === 'ALL_UNASSIGNED') {
      const unassignedGroup = groupedByTeam.find((g) => g.id === 'unassigned')
      if (unassignedGroup) {
        unassignedGroup.workOrders.forEach((wo) => {
          onAssignTeamLead(wo.id, leaderUserId)
        })
      }
    } else if (typeof assigningLeaderForWorkOrderId === 'number') {
      onAssignTeamLead(assigningLeaderForWorkOrderId, leaderUserId)
    }
    setAssigningLeaderForWorkOrderId(null)
  }

  return (
    <div className="flex flex-col border rounded-xl bg-card overflow-hidden shadow-sm">
      {/* Calendar Header: Teams & Crews title + 6 Days */}
      <div className="flex border-b bg-muted/40 text-xs font-semibold">
        <div className="w-48 lg:w-56 p-2.5 border-r flex items-center justify-between font-bold text-muted-foreground shrink-0">
          <span>Teams & Crews</span>
          {isPlanner && onOpenManageRoster && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                onOpenManageRoster({
                  id: undefined,
                  teamName: '',
                  teamLeadId: null,
                  volunteerIds: [],
                })
              }
              className="h-6 text-[10px] px-2 gap-1 bg-background hover:bg-primary hover:text-white font-medium shadow-sm"
              title="Create a new team for this week"
            >
              <Plus className="w-3 h-3" />
              + Team
            </Button>
          )}
        </div>
        <div className="flex-1 grid grid-cols-6 divide-x">
          {weekDates.map((d) => (
            <div
              key={d.date}
              className={cn(
                'p-2.5 text-center flex flex-col items-center justify-center',
                d.isToday && 'bg-primary/10 font-bold text-primary'
              )}
            >
              <span className="text-[11px] text-muted-foreground uppercase">{d.dayName}</span>
              <span className="text-sm font-semibold">{d.dayNumber}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Rows Grouped by Team */}
      <div className="divide-y flex-1 overflow-y-auto">
        {groupedByTeam.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No teams configured yet for this week. Click <strong>+ Team</strong> above to create a team!
          </div>
        ) : (
          groupedByTeam.map((group) => {
            const isUnassigned = group.id === 'unassigned'
            const team = group.team
            const teamName = group.teamName
            const hasLeader = Boolean(group.lead)
            const canRemoveTeam =
              isPlanner &&
              !isUnassigned &&
              team &&
              group.workOrders.length === 0 &&
              onDeleteTeam

            // Multi-lane layout for this team's work orders
            const { placedItems, totalLanes } = computeTeamLanes(group.id, group.workOrders)

            return (
              <div
                key={group.id}
                className={cn(
                  'flex min-h-[140px] hover:bg-muted/5 relative',
                  isUnassigned && 'bg-amber-500/[0.03] border-t-2 border-amber-300/40'
                )}
              >
                {/* Left Row Header: Team info & Leader assignment */}
                <div className="w-48 lg:w-56 p-3 border-r bg-muted/20 flex flex-col justify-between text-xs shrink-0 z-20">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 font-bold text-foreground truncate">
                        <div
                          className={cn(
                            'w-6 h-6 rounded-md flex items-center justify-center text-[10px] shrink-0 font-bold',
                            isUnassigned
                              ? 'bg-amber-100 text-amber-900 border border-amber-300'
                              : 'bg-primary/15 text-primary'
                          )}
                        >
                          {isUnassigned ? (
                            <User className="w-3.5 h-3.5" />
                          ) : (
                            <Users className="w-3.5 h-3.5" />
                          )}
                        </div>
                        <span className="truncate font-semibold">{teamName}</span>
                      </div>

                      {canRemoveTeam && (
                        <button
                          onClick={() => onDeleteTeam && team?.id && onDeleteTeam(team.id)}
                          className="text-muted-foreground hover:text-destructive p-0.5"
                          title="Remove empty team"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {isUnassigned ? (
                      <div>
                        <Badge
                          variant="secondary"
                          className="bg-amber-100 text-amber-900 border border-amber-300 text-[9px] px-1.5 py-0 mb-1"
                        >
                          Needs Team
                        </Badge>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {/* Leader section */}
                        {hasLeader ? (
                          <div className="flex items-center justify-between gap-1 bg-background/80 p-1.5 rounded border">
                            <div className="flex items-center gap-1.5 truncate min-w-0">
                              <div className="w-5 h-5 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[9px] font-bold shrink-0">
                                {group.lead?.fullname?.charAt(0) || 'L'}
                              </div>
                              <div className="truncate">
                                <div className="font-semibold truncate text-[11px] leading-tight text-foreground">
                                  {group.lead?.fullname || 'Team Leader'}
                                </div>
                                {(() => {
                                  const roleStr =
                                    typeof group.lead?.role === 'string'
                                      ? group.lead.role
                                      : group.lead?.role?.name
                                  return roleStr ? (
                                    <div className="text-[9px] text-muted-foreground leading-tight truncate">
                                      {roleStr}
                                    </div>
                                  ) : null
                                })()}
                              </div>
                            </div>
                            {isPlanner && team && (
                              <button
                                onClick={() => setAssigningLeaderForTeamId(team.id)}
                                className="text-[10px] text-muted-foreground hover:text-primary underline shrink-0 font-medium"
                                title="Change assigned leader"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <Badge
                              variant="secondary"
                              className="bg-amber-100 text-amber-900 border border-amber-300 text-[9px] px-1 py-0 mb-0.5"
                            >
                              Needs Team Leader
                            </Badge>

                            {isPlanner && team && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAssigningLeaderForTeamId(team.id)}
                                className="h-6 text-[10px] px-2 w-full bg-white hover:bg-primary hover:text-white border-amber-400 text-amber-900 gap-1"
                              >
                                <UserPlus className="w-3 h-3" />
                                Assign Leader
                              </Button>
                            )}
                          </div>
                        )}

                        {/* Crew Roster badge & Manage button */}
                        {team && (
                          <div className="flex items-center justify-between gap-1 pt-1.5 border-t">
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 h-5 gap-1 bg-muted font-medium text-foreground"
                            >
                              <Users className="w-3 h-3 text-primary" />
                              <span>{team.volunteerIds?.length || 0} Crew</span>
                            </Badge>

                            {onOpenManageRoster && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onOpenManageRoster(team)}
                                className="h-5 text-[10px] px-1.5 gap-1 bg-background hover:bg-primary hover:text-white"
                                title="Manage crew roster for this team"
                              >
                                <Users className="w-2.5 h-2.5" />
                                Manage Crew
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {isPlanner && (
                    <div className="mt-2 pt-2 border-t text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>
                        {group.workOrders.length} job
                        {group.workOrders.length === 1 ? '' : 's'}
                        {totalLanes > 1 && ` (${totalLanes} tracks)`}
                      </span>
                      {!isUnassigned && (
                        <span className="text-[10px] italic">Drag handle to assign</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Calendar Track: Single Unified Drop Surface */}
                <div
                  onDragOver={(e) => {
                    if (!isPlanner) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'

                    const dayIndex = computeDayIndex(e.clientX, e.currentTarget)
                    if (
                      dragOverCell?.rowId !== group.id ||
                      dragOverCell?.dayIndex !== dayIndex
                    ) {
                      setDragOverCell({
                        rowId: group.id,
                        dayIndex,
                        date: weekDates[dayIndex].date,
                      })
                    }
                  }}
                  onDragLeave={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    if (
                      e.clientX < rect.left ||
                      e.clientX >= rect.right ||
                      e.clientY < rect.top ||
                      e.clientY >= rect.bottom
                    ) {
                      if (dragOverCell?.rowId === group.id) {
                        setDragOverCell(null)
                      }
                    }
                  }}
                  onDrop={(e) => {
                    if (!isPlanner) return
                    e.preventDefault()
                    e.stopPropagation()

                    const dayIndex = computeDayIndex(e.clientX, e.currentTarget)
                    setDragOverCell(null)
                    setDraggingWorkOrderId(null)

                    let data = getActiveJobBoardDrag()
                    if (!data) {
                      try {
                        const raw = e.dataTransfer.getData('text/plain')
                        if (raw) data = JSON.parse(raw)
                      } catch {}
                    }
                    setActiveJobBoardDrag(null)
                    if (!data) return

                    const targetTeam = group.team

                    if (data.type === 'MOVE_WORK_ORDER' && data.workOrderId) {
                      const duration = data.duration || 1
                      const newDates: string[] = []
                      for (let i = 0; i < duration; i++) {
                        const idx = Math.min(weekDates.length - 1, dayIndex + i)
                        if (!newDates.includes(weekDates[idx].date)) {
                          newDates.push(weekDates[idx].date)
                        }
                      }
                      onMoveWorkOrder(data.workOrderId, targetTeam, newDates)
                    } else if (data.type === 'NEW_WORK_SCOPE' && data.scope) {
                      if (onDropNewWorkScope) {
                        onDropNewWorkScope(
                          data.scope,
                          targetTeam,
                          weekDates[dayIndex].date
                        )
                      }
                    }
                  }}
                  className="flex-1 relative min-h-[140px]"
                >
                  {/* Background vertical day divider columns */}
                  <div className="absolute inset-0 grid grid-cols-6 divide-x pointer-events-none">
                    {weekDates.map((day, dayIndex) => {
                      const isOver =
                        dragOverCell?.rowId === group.id &&
                        dragOverCell?.dayIndex === dayIndex

                      return (
                        <div
                          key={day.date}
                          className={cn(
                            'h-full transition-all flex flex-col justify-end p-1.5',
                            day.isToday && 'bg-primary/[0.03]',
                            isOver &&
                              'bg-primary/20 ring-2 ring-inset ring-primary border-primary font-bold'
                          )}
                        >
                          {isOver && (
                            <div className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded text-center shadow-md animate-pulse">
                              Drop to snap to {day.dayName}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Empty row placeholder */}
                  {group.workOrders.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center p-4 text-xs text-muted-foreground pointer-events-none z-10">
                      <div className="border border-dashed border-muted-foreground/30 bg-muted/10 rounded-lg p-3 text-center max-w-sm">
                        {isUnassigned
                          ? 'No unassigned jobs • All jobs are assigned to teams'
                          : 'No jobs scheduled yet • Drag handle here or schedule from Planning Drawer'}
                      </div>
                    </div>
                  )}

                  {/* Foreground Work Order Multi-Day Spanning Boxes in Ordered Lanes */}
                  <div
                    className="relative z-10 grid grid-cols-6 gap-2 p-2.5"
                    style={{
                      gridTemplateRows: `repeat(${totalLanes}, minmax(0, auto))`,
                    }}
                  >
                    {placedItems.map(
                      ({ wo, colStart, colSpan, laneIndex, startIndex, endIndex }) => {
                        const homeownerName =
                          wo.home?.homeowner?.user?.fullname ||
                          wo.home?.homeowner?.name ||
                          'Homeowner'
                        const workScopeName =
                          wo.workScope?.workScopeDisplay ||
                          wo.workOrderType ||
                          'Work Order'

                        const neededCount = wo.volunteersNeeded || 0
                        const approval = wo.approvalStatus || 'SCHEDULED'
                        const isMultiDay = colSpan > 1
                        const isBeingDragged = draggingWorkOrderId === wo.id
                        const hasNoLeader = !wo.teamLead?.id

                        // Compute active dates within this week
                        const activeWeekDays = weekDates.slice(
                          startIndex,
                          endIndex + 1
                        )

                        const hasMultipleJobsInRow = group.workOrders.length > 1

                        return (
                          <div
                            key={wo.id}
                            onClick={() => {
                              if (regionSlug) {
                                router.push(buildRegionUrl(regionSlug, `/work-orders/${wo.id}`))
                              }
                            }}
                            style={{
                              gridColumnStart: colStart,
                              gridColumnEnd: `span ${colSpan}`,
                              gridRowStart: laneIndex + 1,
                            }}
                            className={cn(
                              'group relative p-2.5 rounded-xl border text-xs shadow-sm hover:shadow-md transition-all flex flex-col justify-between gap-2 select-none cursor-pointer',
                              isBeingDragged &&
                                'opacity-40 scale-[0.98] ring-2 ring-primary',
                              hasNoLeader
                                ? 'border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-700'
                                : approval === 'COMPLETED'
                                  ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-700'
                                  : approval === 'PENDING_REVIEW'
                                    ? 'border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-700'
                                    : 'border-border bg-card hover:border-primary/60'
                            )}
                          >
                            {/* Top: Drag handle, Vertical reorder buttons, Scope, Duration, Leader Pill & Approval Pill */}
                            <div className="flex items-start justify-between gap-1 flex-wrap">
                              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                {/* Dedicated Drag Handle: Only dragging from here initiates DnD */}
                                {isPlanner && (
                                  <div
                                    draggable={isPlanner}
                                    onDragStart={(e) => {
                                      e.stopPropagation()
                                      setDraggingWorkOrderId(wo.id)
                                      const payload = {
                                        type: 'MOVE_WORK_ORDER' as const,
                                        workOrderId: wo.id,
                                        duration: colSpan,
                                        currentLeaderId: wo.teamLead?.id || null,
                                      }
                                      setActiveJobBoardDrag(payload)
                                      e.dataTransfer.setData(
                                        'text/plain',
                                        JSON.stringify(payload)
                                      )
                                      e.dataTransfer.effectAllowed = 'move'
                                    }}
                                    onDragEnd={(e) => {
                                      e.stopPropagation()
                                      setDraggingWorkOrderId(null)
                                      setDragOverCell(null)
                                      setActiveJobBoardDrag(null)
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                                    title="Drag handle to reschedule or assign to leader"
                                  >
                                    <GripHorizontal className="w-3.5 h-3.5" />
                                  </div>
                                )}

                                {/* Vertical Track Up/Down Reordering Buttons */}
                                {isPlanner && hasMultipleJobsInRow && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center opacity-40 group-hover:opacity-100 transition-opacity bg-muted rounded p-0.5 gap-0.5"
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleMoveOrderVertically(group.id, wo.id, 'up')
                                      }}
                                      className="p-0.5 hover:bg-background rounded hover:text-foreground text-muted-foreground"
                                      title="Move work order up vertically"
                                    >
                                      <ChevronUp className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleMoveOrderVertically(group.id, wo.id, 'down')
                                      }}
                                      className="p-0.5 hover:bg-background rounded hover:text-foreground text-muted-foreground"
                                      title="Move work order down vertically"
                                    >
                                      <ChevronDown className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}

                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0 font-bold bg-primary/10 text-primary border-primary/30 truncate"
                                >
                                  {workScopeName}
                                </Badge>

                                {isMultiDay && (
                                  <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.2 rounded">
                                    {colSpan} Days ({activeWeekDays[0]?.dayName}–
                                    {
                                      activeWeekDays[activeWeekDays.length - 1]
                                        ?.dayName
                                    }
                                    )
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-1 shrink-0">
                                {/* 1-Click Leader Assignment Button if unassigned */}
                                {hasNoLeader && isPlanner && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setAssigningLeaderForWorkOrderId(wo.id)
                                    }}
                                    className="h-5 text-[9px] px-1.5 bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200 gap-0.5"
                                  >
                                    <UserPlus className="w-2.5 h-2.5" />
                                    Assign Lead
                                  </Button>
                                )}

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onOpenApprovalModal(wo)
                                  }}
                                  title="Click to view/update approval status"
                                >
                                  {approval === 'COMPLETED' ? (
                                    <Badge className="bg-emerald-600 text-white text-[9px] px-1 py-0 h-4 gap-0.5">
                                      <CheckCircle2 className="w-2.5 h-2.5" /> Done
                                    </Badge>
                                  ) : approval === 'PENDING_REVIEW' ? (
                                    <Badge
                                      variant="secondary"
                                      className="bg-amber-100 text-amber-900 border border-amber-300 text-[9px] px-1 py-0 h-4 gap-0.5"
                                    >
                                      <Clock className="w-2.5 h-2.5" /> Review
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="text-[9px] px-1 py-0 h-4 text-muted-foreground"
                                    >
                                      Active
                                    </Badge>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Middle: Homeowner & Address */}
                            <div className="space-y-0.5">
                              <div className="font-bold text-foreground text-xs truncate group-hover:text-primary transition-colors">
                                {homeownerName}
                              </div>
                              {wo.home?.address && (
                                <div className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                                  <MapPin className="w-3 h-3 text-primary shrink-0" />
                                  <span className="truncate">{wo.home.address}</span>
                                </div>
                              )}
                            </div>

                            {/* Bottom: Daily Staffing Chips across scheduled days */}
                            <div className="pt-1.5 border-t flex items-center justify-between gap-2 flex-wrap text-[11px]">
                              <div className="flex items-center gap-1 flex-wrap">
                                {activeWeekDays.map((day) => {
                                  let assignedCount = 0
                                  let shift = 'FULL_DAY'
                                  if (Array.isArray(wo.workOrderHours)) {
                                    const entry = wo.workOrderHours.find(
                                      (h: any) => h.date === day.date
                                    )
                                    if (
                                      entry &&
                                      Array.isArray(entry.volunteerIds)
                                    ) {
                                      assignedCount = entry.volunteerIds.length
                                    }
                                    if (entry?.shift) {
                                      shift = entry.shift
                                    }
                                  } else if (Array.isArray(wo.volunteers)) {
                                    assignedCount = wo.volunteers.length
                                  }

                                  const isFilled =
                                    neededCount > 0 && assignedCount >= neededCount

                                  return (
                                    <button
                                      key={day.date}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        onOpenStaffingModal(wo, day.date)
                                      }}
                                      className={cn(
                                        'px-1.5 py-0.5 rounded border text-[10px] font-medium flex items-center gap-1 hover:border-primary transition-colors',
                                        isFilled
                                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300'
                                          : 'bg-muted/60 text-muted-foreground border-border'
                                      )}
                                      title={`Click to staff volunteers on ${day.dayName} ${day.dayNumber} (${shift === 'MORNING' ? 'Morning Shift' : shift === 'AFTERNOON' ? 'Afternoon Shift' : 'Full Day'})`}
                                    >
                                      <span>{day.dayName}:</span>
                                      {shift === 'MORNING' && (
                                        <span className="text-[9px] font-bold px-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-950 dark:text-amber-300">
                                          AM
                                        </span>
                                      )}
                                      {shift === 'AFTERNOON' && (
                                        <span className="text-[9px] font-bold px-0.5 rounded bg-indigo-100 text-indigo-900 border border-indigo-300 dark:bg-indigo-950 dark:text-indigo-300">
                                          PM
                                        </span>
                                      )}
                                      <Users className="w-2.5 h-2.5" />
                                      <span>
                                        {assignedCount}
                                        {neededCount > 0 ? `/${neededCount}` : ''}
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>

                            {/* Multi-Day Duration Resize Controls (Planner only) */}
                            {isPlanner && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity ml-auto"
                              >
                                <button
                                  onClick={(e) => handleShortenDuration(e, wo)}
                                  className="p-1 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                                  title="Shorten span (-1 day)"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => handleExtendDuration(e, wo)}
                                  className="p-1 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                                  title="Extend span (+1 day)"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    }
                  )}
                </div>
              </div>
            </div>
          )
        })
      )}
    </div>

      {/* Assign Team Leader to Team Modal */}
      {assigningLeaderForTeamId !== null && (
        <Dialog
          open={assigningLeaderForTeamId !== null}
          onOpenChange={(open) => {
            if (!open) setAssigningLeaderForTeamId(null)
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <UserCheck className="w-4 h-4 text-primary" />
                Assign Team Leader
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 pt-2">
              <p className="text-xs text-muted-foreground">
                Select an identified Team Leader or on-site volunteer to assign to this team.
              </p>

              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={leaderSearch}
                  onChange={(e) => setLeaderSearch((e.target as HTMLInputElement).value)}
                  placeholder="Search volunteers & candidate leads..."
                  className="h-8 pl-8 text-xs"
                />
              </div>

              <div className="max-h-64 overflow-y-auto divide-y border rounded-lg">
                {availableVolunteers.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No available volunteers found
                  </div>
                ) : (
                  availableVolunteers.map((v) => {
                    const userId = v.userId || v.id
                    const isCandidate = (v.experienceLeadingTeams || 0) >= 3

                    return (
                      <div
                        key={userId}
                        className="p-2.5 flex items-center justify-between hover:bg-muted/20 text-xs"
                      >
                        <div>
                          <div className="font-semibold flex items-center gap-1.5">
                            {v.fullName || v.fullname}
                            {isCandidate && (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-300"
                              >
                                Lead Rating {v.experienceLeadingTeams}/5
                              </Badge>
                            )}
                          </div>
                          {v.groupName && (
                            <div className="text-[11px] text-muted-foreground">
                              {v.groupName}
                            </div>
                          )}
                        </div>

                        <Button
                          size="sm"
                          onClick={() => handleSelectLeaderForTeam(userId)}
                          className="h-7 text-xs px-2.5 bg-primary hover:bg-primary/90 text-white gap-1"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          Assign
                        </Button>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Assign Leader to Work Order Dialog */}
      {assigningLeaderForWorkOrderId !== null && (
        <Dialog
          open={assigningLeaderForWorkOrderId !== null}
          onOpenChange={(open) => {
            if (!open) setAssigningLeaderForWorkOrderId(null)
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <UserCheck className="w-4 h-4 text-primary" />
                Select Team Leader
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 pt-2">
              <p className="text-xs text-muted-foreground">
                Choose an identified Team Leader or on-site volunteer to assign this job to.
              </p>

              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={leaderSearch}
                  onChange={(e) => setLeaderSearch((e.target as HTMLInputElement).value)}
                  placeholder="Search available leaders..."
                  className="h-8 pl-8 text-xs"
                />
              </div>

              <div className="max-h-64 overflow-y-auto divide-y border rounded-lg">
                {availableVolunteers.map((v) => {
                  const userId = v.userId || v.id
                  const isCandidate = (v.experienceLeadingTeams || 0) >= 3

                  return (
                    <div
                      key={userId}
                      className="p-2.5 flex items-center justify-between hover:bg-muted/20 text-xs"
                    >
                      <div>
                        <div className="font-semibold flex items-center gap-1.5">
                          {v.fullName || v.fullname}
                          {isCandidate && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-300"
                            >
                              Lead Rating {v.experienceLeadingTeams}/5
                            </Badge>
                          )}
                        </div>
                        {v.groupName && (
                          <div className="text-[11px] text-muted-foreground">
                            {v.groupName}
                          </div>
                        )}
                      </div>

                      <Button
                        size="sm"
                        onClick={() => handleSelectLeaderForWorkOrder(userId)}
                        className="h-7 text-xs px-2.5 bg-primary hover:bg-primary/90 text-white gap-1"
                      >
                        <UserCheck className="w-3.5 h-3.5" />
                        Assign
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
